import { useState, useCallback, useEffect, type ReactNode } from 'react'
import { AuthContext, type AuthContextType } from './auth-context'
import { apiClient } from '@/lib/api/client'
import { clearSessionSnapshot } from '@/lib/session-restore'
import type { User } from '@/lib/api/types'
import Bugsnag from '@bugsnag/js'

const TOKEN_KEY = 'eventpay_token'
const USER_KEY = 'eventpay_user'
const PERMISSIONS_KEY = 'eventpay_permissions'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_KEY)
    return stored ? JSON.parse(stored) : null
  })
  
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(TOKEN_KEY)
  })
  
  const [permissions, setPermissions] = useState<string[]>(() => {
    const stored = localStorage.getItem(PERMISSIONS_KEY)
    return stored ? JSON.parse(stored) : []
  })
  
  const [isLoading, setIsLoading] = useState(true)

  // Check if token is still valid on mount
  useEffect(() => {
    const validateSession = async () => {
      if (token) {
        try {
          const response = await apiClient.getCurrentUser(token)
          if (response.success && response.user) {
            setUser(response.user)
            if (response.permissions) {
              setPermissions(response.permissions)
              localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(response.permissions))
            }
          } else {
            // Token is invalid, clear session
            handleLogout()
          }
        } catch {
          // Network error or invalid token
          handleLogout()
        }
      }
      setIsLoading(false)
    }

    validateSession()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync user context to Bugsnag for error attribution
  useEffect(() => {
    if (!Bugsnag.isStarted()) return
    if (user) {
      Bugsnag.setUser(String(user.id), user.email, `${user.first_name} ${user.last_name}`)
      Bugsnag.addMetadata('user_context', {
        type: user.type,
        is_superuser: user.is_superuser,
      })
    } else {
      Bugsnag.setUser(undefined, undefined, undefined)
      Bugsnag.clearMetadata('user_context')
    }
  }, [user])

  const handleLogin = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const response = await apiClient.authenticateUser(email, password)
      
      if (response.success && response.token && response.user) {
        setToken(response.token)
        setUser(response.user)
        localStorage.setItem(TOKEN_KEY, response.token)
        localStorage.setItem(USER_KEY, JSON.stringify(response.user))
        
        // Fetch permissions after login
        const userResponse = await apiClient.getCurrentUser(response.token)
        if (userResponse.permissions) {
          setPermissions(userResponse.permissions)
          localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(userResponse.permissions))
        }
      } else {
        throw new Error(response.error || 'Login failed')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleLogout = useCallback(() => {
    setToken(null)
    setUser(null)
    setPermissions([])
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(PERMISSIONS_KEY)
    // The next login (possibly a different operator) must not inherit
    // this session's event restore target.
    clearSessionSnapshot()
    // Redirect to login page after clearing auth state
    window.location.href = '/auth/login'
  }, [])

  const refreshPermissions = useCallback(async () => {
    if (!token) return
    
    try {
      const response = await apiClient.getCurrentUser(token)
      if (response.permissions) {
        setPermissions(response.permissions)
        localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(response.permissions))
      }
    } catch (error) {
      console.error('Failed to refresh permissions:', error)
    }
  }, [token])

  const value: AuthContextType = {
    user,
    token,
    permissions,
    isAuthenticated: !!token && !!user,
    isLoading,
    login: handleLogin,
    logout: handleLogout,
    refreshPermissions,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
